import React, { useState } from 'react';
import { Wallet } from 'lucide-react';
import CapitalHistoryChart from '../../components/analytics/CapitalHistoryChart';
import DailyPnlTable from '../../components/analytics/DailyPnlTable';
import KpiStripCard from '../../components/analytics/KpiStripCard';
import EquityCurveCard from '../../components/analytics/EquityCurveCard';
import DrawdownCard from '../../components/analytics/DrawdownCard';
import MonthlyHeatmap from '../../components/analytics/MonthlyHeatmap';
import EmptyState from '../../components/analytics/EmptyState';
import SetCapitalDialog from '../../components/broker/SetCapitalDialog';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAnalyticsData } from '../../context/AnalyticsDataContext';

function formatPeriod(start: string, end: string): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

const OverviewSection: React.FC = () => {
  const { filters, summary, capital, dailyPoints, brokerAccounts, refetchBrokerAccounts } = useAnalyticsData();
  const hasData = !!summary && summary.totalTrades > 0;

  const [capitalDialogOpen, setCapitalDialogOpen] = useState(false);

  const hasMissingCapital = brokerAccounts.length > 0
    && brokerAccounts.some((a) => !a.capital || a.capital <= 0);

  return (
    <>
      {hasMissingCapital && (
        <div className="bg-blue-50/60 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 h-9 w-9 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Set your trading capital
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                Enable Total Return %, CAGR and Sharpe ratio by setting how much capital
                you've allocated to algo trading on each account.
              </p>
            </div>
            <div className="shrink-0">
              <Button variant="primary" size="sm" onClick={() => setCapitalDialogOpen(true)}>
                Set capital
              </Button>
            </div>
          </div>
        </div>
      )}

      <SetCapitalDialog
        isOpen={capitalDialogOpen}
        onClose={() => setCapitalDialogOpen(false)}
        accounts={brokerAccounts}
        onSaved={refetchBrokerAccounts}
      />

      {summary && hasData && (
        <>
          {/* Row 1 — KPIs span the full width. */}
          <KpiStripCard
            summary={summary}
            title="Live Performance"
            period={formatPeriod(filters.start, filters.end)}
          />

          {/* Row 2 — Daily P&L (narrow left) | Equity + DD + Capital (right).
              `items-stretch` (grid default) equalises column heights, and
              the right column uses flex-col with flex-1 on the Equity +
              Drawdown cards so they grow to match the table's height.
              Capital deployed keeps its natural height. */}
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
            <div className="lg:col-span-2 min-w-0">
              <Card>
                <CardHeader>
                  <CardTitle>Daily P&amp;L</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <DailyPnlTable
                    points={dailyPoints}
                    rangeStart={filters.start}
                    rangeEnd={filters.end}
                  />
                </CardContent>
              </Card>
            </div>
            <div className="lg:col-span-5 min-w-0 flex flex-col gap-4">
              <div className="flex-1 min-h-[200px]">
                <EquityCurveCard points={summary.dailyEquity} fill />
              </div>
              <div className="flex-1 min-h-[180px]">
                <DrawdownCard points={summary.dailyDrawdown} fill />
              </div>
              <div className="flex-1 min-h-[180px]">
                <CapitalHistoryChart data={capital} fill />
              </div>
            </div>
          </div>

          {/* Row 3 — Year × Month heatmap, full width. Sourced from
              summary.monthlyReturns so no extra fetch is needed. */}
          <MonthlyHeatmap months={summary.monthlyReturns} />
        </>
      )}

      {!hasData && (
        <Card>
          <CardContent>
            <EmptyState message="No closed trades in this range." />
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default OverviewSection;
